import { useContext, createContext, useEffect, useState, useRef } from 'react';
import {
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { auth } from '../components/firebaseauth';
import { 
  saveAuthToCookies, 
  getAuthFromCookies, 
  clearAuthCookies, 
  isAuthenticated,
  checkCookieIntegrity 
} from '../utils/auth-cookie-manager';

const AuthContext = createContext();

export const AuthContextProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [sessionTerminated, setSessionTerminated] = useState(false);
  const cookieCheckInterval = useRef(null);

  const googleSignIn = async () => {
    if (!auth) {
      const error = new Error("Firebase Authentication is not initialized");
      setAuthError(error);
      throw error;
    }
    
    try {
      setLoading(true);
      // Reset session terminated flag when user is trying to sign in again
      setSessionTerminated(false);
      
      const provider = new GoogleAuthProvider();
      provider.addScope('profile');
      provider.addScope('email');
      
      provider.setCustomParameters({
        prompt: 'select_account'
      });
      
      // Try popup first (better user experience and easier to debug)
      try {
        // const result = await signInWithPopup(auth, provider);
        const result = await signInWithPopup(auth, provider);
        // console.log("Popup sign-in successful!", result.user);
        
        // Save authentication data to cookies
        saveAuthToCookies(result.user);
        
        return result;
      } catch (popupError) {
        console.warn("Popup sign-in failed, falling back to redirect:", popupError);
        // Fall back to redirect
        await signInWithRedirect(auth, provider);
      }
    } catch (error) {
      // console.error("Google Sign-in Error:", error);
      setAuthError(error);
      setLoading(false);
      throw error;
    }
  };

  const logOut = async () => {
    if (!auth) {
      const error = new Error("Firebase Authentication is not initialized");
      setAuthError(error);
      throw error;
    }
    
    try {
      setLoading(true);
      // Sign out from Firebase
      await signOut(auth);
      // Clear cookies
      clearAuthCookies();
      setUser(null);
    } catch (error) {
      // console.error("Log Out Error:", error);
      setAuthError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Cookie integrity check - detects cookie deletion
  useEffect(() => {
    const setupCookieCheck = () => {
      // Don't set up the interval if user is not authenticated
      if (!user) return;
      
      // Clear any existing interval
      if (cookieCheckInterval.current) {
        clearInterval(cookieCheckInterval.current);
      }
      
      // Set up periodic cookie checking (every 5 seconds)
      cookieCheckInterval.current = setInterval(() => {
        const cookiesValid = checkCookieIntegrity();
        const authStillValid = isAuthenticated();
        
        // If auth cookies were deleted but user is still logged in
        if (!authStillValid && user) {
          console.warn("Session termination detected: Auth cookies were cleared");
          setSessionTerminated(true);
          
          // Terminate the session
          setUser(null);
          
          // Also trigger Firebase logout to keep everything in sync
          signOut(auth).catch(err => {
            // console.error("Error signing out after cookie deletion:", err);
          });
        }
      }, 5000);
    };
    
    setupCookieCheck();
    
    // Cleanup
    return () => {
      if (cookieCheckInterval.current) {
        clearInterval(cookieCheckInterval.current);
      }
    };
  }, [user]);

  // Check for existing authentication
  useEffect(() => {
    const checkExistingAuth = () => {
      // Try to get auth from cookies first
      if (isAuthenticated()) {
        const cookieAuth = getAuthFromCookies();
        // console.log("Found authentication in cookies:", cookieAuth);
        // This is a simplified user object - Firebase will provide the full one via onAuthStateChanged
      }
    };

    checkExistingAuth();
  }, []);

  // Set up Firebase auth state listener
  useEffect(() => {
    if (!auth) {
      console.warn("Firebase Authentication is not initialized.");
      setLoading(false);
      return () => {};
    }
    
    const checkRedirectResult = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result) {
          // console.log('Redirect Result Success:', result.user);
          
          // Save authentication data to cookies after redirect
          saveAuthToCookies(result.user);
          setSessionTerminated(false);
          setUser(result.user);
        }
      } catch (error) {
        // console.error('Redirect Error:', error);
        setAuthError(error);
      } finally {
        setLoading(false);
      }
    };

    checkRedirectResult();

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      // console.log('Auth State Changed:', currentUser);
      
      if (currentUser) {
        // Save or update auth data in cookies whenever auth state changes
        saveAuthToCookies(currentUser);
        setSessionTerminated(false);
      } else {
        // Clear cookies when user is null (signed out)
        clearAuthCookies();
      }
      
      setUser(currentUser);
      setLoading(false);
    }, (error) => {
      // console.error("Auth State Change Error:", error);
      setAuthError(error);
      setLoading(false);
    });
    
    return () => {
      unsubscribe();
    };
  }, []);

  return (
    <AuthContext.Provider value={{ 
      googleSignIn, 
      logOut, 
      user, 
      loading, 
      authError,
      sessionTerminated,
      isAuthenticated: () => isAuthenticated() 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const UserAuth = () => {
  return useContext(AuthContext);
};